/**
 * Playlists an administrator shared with this account, and the signage API's
 * errors, for the Apple TV pages.
 *
 * Every FranchiseSign key belongs to one user, and an ADMIN key lists only that
 * user's own playlists and media. The one exception is a playlist a SUPER_ADMIN
 * assigned to one of this account's screens or groups: it is NOT in
 * GET /playlists, but GET /playlists/:id returns it read-only. So a screen or
 * group whose playlistId is not in the account's own list is showing a shared
 * playlist, and this fetches it by id to show its name and slides.
 */
import { useEffect, useState } from 'react';
import { getErrorMessage } from '@/services/apiClient';
import {
  getPlaylist,
  type AppleTvPlaylist,
  type AppleTvPlaylistWithItems,
} from '@/services/appleTv.service';

export const SHARED_PLAYLIST_BADGE = 'Shared by Corporate';

export const SHARED_PLAYLIST_WARNING =
  "This playlist was assigned by Corporate. You won't be able to reselect it.";

export type SharedPlaylist =
  | { status: 'loading' }
  | { status: 'ok'; data: AppleTvPlaylistWithItems }
  /** 404: not this account's, or gone. The two are deliberately indistinguishable. */
  | { status: 'unavailable' }
  /** Any other failure (5xx, timeout, 403): the playlist may be fine, so offer a retry. */
  | { status: 'error'; retry?: () => void };

/**
 * The signage API's status and message. FranchiseSign answers with
 * `{ error: "…" }`, a plain string that getErrorMessage (which reads
 * `error.message`) misses; the proxy passes it back, sometimes rewrapped under
 * `data` by the API client.
 */
export function signageError(error: unknown): { status: number | null; message: string | null } {
  const response = (error as { response?: { status?: number; data?: unknown } })?.response;
  const data = response?.data as { error?: unknown; data?: { error?: unknown } } | undefined;
  const raw = typeof data?.error === 'string' ? data.error : data?.data?.error;
  return {
    status: typeof response?.status === 'number' ? response.status : null,
    message: typeof raw === 'string' && raw.trim() !== '' ? raw : null,
  };
}

export function signageErrorMessage(error: unknown, fallback: string): string {
  return signageError(error).message ?? getErrorMessage(error, fallback);
}

/** 404 on an assign or add: the playlist or media is not this account's any more, or gone. */
export function isNoLongerAvailable(error: unknown): boolean {
  return signageError(error).status === 404;
}

/** Only a 404 means the playlist is gone; anything else is a failed load. */
export function sharedPlaylistFailure(error: unknown, retry?: () => void): SharedPlaylist {
  return isNoLongerAvailable(error) ? { status: 'unavailable' } : { status: 'error', retry };
}

/**
 * The playlists referenced by screens and groups that are not in the account's
 * own list, fetched once each by id. Keyed by playlist id.
 */
export function useSharedPlaylists(
  own: AppleTvPlaylist[],
  referenced: Array<number | null | undefined>,
  enabled = true,
): Record<number, SharedPlaylist> {
  const [shared, setShared] = useState<Record<number, SharedPlaylist>>({});

  const ownIds = new Set(own.map((p) => p.id));
  const missing = [
    ...new Set(referenced.filter((id): id is number => id != null && !ownIds.has(id))),
  ].sort((a, b) => a - b);
  const key = missing.join(',');

  useEffect(() => {
    if (!enabled || key === '') return;
    let cancelled = false;
    const ids = key.split(',').map(Number);
    setShared((prev) => {
      const next = { ...prev };
      for (const id of ids) if (!next[id]) next[id] = { status: 'loading' };
      return next;
    });
    const load = (id: number) => {
      const retry = () => {
        if (cancelled) return;
        setShared((prev) => ({ ...prev, [id]: { status: 'loading' } }));
        load(id);
      };
      getPlaylist(id)
        .then((data) => {
          if (!cancelled) setShared((prev) => ({ ...prev, [id]: { status: 'ok', data } }));
        })
        .catch((error) => {
          if (!cancelled) setShared((prev) => ({ ...prev, [id]: sharedPlaylistFailure(error, retry) }));
        });
    };
    for (const id of ids) load(id);
    return () => {
      cancelled = true;
    };
  }, [key, enabled]);

  return shared;
}

/** A shared playlist's name for a label, or a placeholder while it loads or if it is gone. */
export function sharedPlaylistName(entry: SharedPlaylist | undefined, id: number): string {
  if (entry?.status === 'ok') return entry.data.playlist.name;
  if (entry?.status === 'unavailable') return 'Playlist no longer available';
  return `Playlist #${id}`;
}
